import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@shadcn-in-nx/ui/input";
import { Label } from "@shadcn-in-nx/ui/label";
import { Button } from "@shadcn-in-nx/ui/button";
import { useBluelagoon } from "@bluelagoon";
import { toast } from "sonner";

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export default function TriggerScheduleSection({ trigger, workflowId }) {
    const { t } = useTranslation();
    const config = trigger.timeConfig || {};
    const [timeOfDay, setTimeOfDay] = useState(config.timeOfDay || "09:00");
    const [timezone, setTimezone] = useState(() => config.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone);
    const [weekdays, setWeekdays] = useState(config.weekdays || []);
    const [cron, setCron] = useState(config.cron || "");
    const [saving, setSaving] = useState(false);
    const { call: updateTrigger } = useBluelagoon({ method: "updateWorkflowTrigger" });

    const isRecurring = !!cron;

    const toggleWeekday = useCallback((day) => {
        setWeekdays((prev) => prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]);
    }, []);

    const handleSave = useCallback(async () => {
        setSaving(true);
        try {
            const payload = { workflowId, type: "TIME", workflowTriggerId: trigger.id, config: { timezone } };
            if (isRecurring) {
                payload.config.cron = cron;
            } else {
                payload.config.timeOfDay = timeOfDay;
                payload.config.weekdays = weekdays;
            }
            await updateTrigger(payload);
            toast.success(t("chatInput.scheduleUpdated"));
        } catch {
            toast.error(t("chatInput.scheduleUpdateFailed"));
        }
        setSaving(false);
    }, [workflowId, trigger.id, updateTrigger, isRecurring, cron, timeOfDay, timezone, weekdays, t]);

    return (
        <div className="p-4 space-y-4">
            {!isRecurring ? (
                <>
                    <div>
                        <Label className="text-xs">{t("chatInput.timeOfDay")}</Label>
                        <Input className="mt-1" type="time" value={timeOfDay} onChange={(e) => setTimeOfDay(e.target.value)} />
                    </div>
                    <div>
                        <Label className="text-xs">{t("chatInput.timezone")}</Label>
                        <Input className="mt-1 text-sm" value={timezone} onChange={(e) => setTimezone(e.target.value)} />
                    </div>
                    <div>
                        <Label className="text-xs mb-2 block">{t("chatInput.days")}</Label>
                        <div className="flex flex-wrap gap-1">
                            {WEEKDAYS.map((day) => (
                                <button
                                    key={day}
                                    onClick={() => toggleWeekday(day)}
                                    className={`px-2 py-1 text-xs rounded-md border transition-colors ${
                                        weekdays.includes(day)
                                            ? "bg-blue-50 border-blue-300 text-blue-700"
                                            : "bg-white border-gray-200 text-gray-500 hover:border-gray-300"
                                    }`}
                                >
                                    {t(`chatInput.weekdayShort.${day}`)}
                                </button>
                            ))}
                        </div>
                    </div>
                </>
            ) : (
                <div>
                    <Label className="text-xs">{t("chatInput.cronExpression")}</Label>
                    <Input className="mt-1 font-mono text-sm" value={cron} onChange={(e) => setCron(e.target.value)} placeholder="*/15 * * * *" />
                    <p className="text-[10px] text-gray-400 mt-1">{t("chatInput.cronExample")}</p>
                </div>
            )}
            {config.nextRunTimeUtc && (
                <p className="text-xs text-gray-400">{t("chatInput.nextRun", { time: new Date(config.nextRunTimeUtc).toLocaleString() })}</p>
            )}
            <Button size="sm" onClick={handleSave} disabled={saving} className="w-full">
                {saving ? t("chatInput.saving") : t("chatInput.saveSchedule")}
            </Button>
        </div>
    );
}
